import React, { useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import isNil from 'lodash/isNil';
import PlacementExportMenu from 'src/components/shared/placementExportMenu';
import PlacementUpdateMenu from 'src/components/shared/placementUpdateMenu';
import { TRANSFER_PENDING } from 'src/config/AccommodationStatusNames';
import * as LDAP from 'src/config/LDAP';
import { setJmrFlowType } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import {
  setIsConfirmDeleteModalOpen,
  setIsEmailModalOpen,
} from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { constructRosieCaseLink } from 'src/utils/constructRTWLinks';
import { CaseSources, JmrFlowTypes } from 'src/utils/jmrUtils';

/**
 * Default component
 */
const TopOptions = () => {
  const { placementId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const updateButtonRef = useRef<HTMLButtonElement>(null);
  const { ldap } = useAppSelector((state) => state.user);
  const { consolidatedRegionTasks } = useAppSelector((state) => state.newJmrPage);
  const {
    placementDetails,
    accommodationStatus: accommodationStatusesForSite,
    placementSite,
  } = useAppSelector((state) => state.placementDetailsPage);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isUpdateMenuOpen, setIsUpdateMenuOpen] = useState(false);
  const isAdmin = ldap.includes(LDAP.ADMIN);

  return (
    <Row maxWidth="100%" widths={['', 'fill']}>
      {/* Associate/Placement Info */}
      <Text type="h400" data-cy="placement-details__name-header">
        Case ID: {placementDetails.accommodationType} |{' '}
        {!isNil(placementDetails.externalCaseId) && placementDetails.accommodationType === CaseSources.Austin ? (
          <Tooltip title="Open case in AUSTIN" position="bottom">
            <Link href={constructRosieCaseLink(placementDetails.externalCaseId)}>
              {placementDetails.externalCaseId}
            </Link>
          </Tooltip>
        ) : (
          placementDetails.externalCaseId
        )}
      </Text>
      <div>
        <Tag type="warning" data-cy="placement-details__status-tag">
          {placementDetails?.accommodationStatus?.status}
        </Tag>
      </div>

      {/* Delete Placement */}
      {isAdmin && (
        <Button
          data-cy="placement-details__delete-btn"
          type="link"
          onClick={() => dispatch(setIsConfirmDeleteModalOpen(true))}
        >
          Delete Placement
        </Button>
      )}

      {/* Send Email/Update/Export */}
      {placementDetails?.accommodationStatus?.status !== TRANSFER_PENDING && (
        <>
          <Button
            type="secondary"
            onClick={() => dispatch(setIsEmailModalOpen(true))}
            data-cy="placement-detail-send-email__btn"
          >
            Send Email
          </Button>
          <Button
            id="update-placement-open-menu-btn"
            data-cy="placement-detail__update-btn"
            type="primary"
            ref={updateButtonRef}
            onClick={() => setIsUpdateMenuOpen(true)}
          >
            Update
            <Icon tokens={chevronDownSmallTokens} />
          </Button>
          <Button type="primary" ref={exportButtonRef} onClick={() => setIsExportMenuOpen(true)}>
            Export
            <Icon tokens={chevronDownSmallTokens} />
          </Button>
        </>
      )}

      {/* Accept Transfer */}
      {placementDetails?.accommodationStatus?.status === TRANSFER_PENDING && (
        <Button
          type="primary"
          data-cy="placement-details__accept-transfer-btn"
          onClick={() => {
            dispatch(setJmrFlowType(JmrFlowTypes.CompleteTransfer));
            navigate(`/newJMR/${placementId}/update/${JmrFlowTypes.CompleteTransfer}`);
          }}
        >
          Accept Transfer
        </Button>
      )}

      <PlacementUpdateMenu
        isMenuOpen={isUpdateMenuOpen}
        closeMenu={() => setIsUpdateMenuOpen(false)}
        placementId={placementId!}
        anchorRef={updateButtonRef}
        accommodationStatusesForSite={accommodationStatusesForSite ?? []}
        managerAlias={placementDetails.managerAlias}
        fc={placementDetails.fc}
        accommodationType={placementDetails.accommodationType}
        placementStatus={placementDetails.accommodationStatus?.status}
        workRestrictions={placementDetails.workRestrictions}
        modifiedShiftLength={placementDetails.modifiedShiftLength}
        externalCaseId={placementDetails.externalCaseId}
        regionTasks={consolidatedRegionTasks}
      />

      <PlacementExportMenu
        isMenuOpen={isExportMenuOpen}
        closeMenu={() => setIsExportMenuOpen(false)}
        placementId={placementId!}
        anchorRef={exportButtonRef}
        newestJmrFileName={placementDetails.newestJmrFileName}
        countryId={placementDetails.countryId}
        allocatedJobShifts={placementDetails.allocatedJobShifts}
        jobModificationsEnabled={placementSite?.jobModificationsEnabled}
      />
    </Row>
  );
};

export default TopOptions;
